import React, { useState } from 'react';
import {
  Box, VStack, HStack, Heading, Text, Button, Radio, RadioGroup,
  Card, CardBody, CardHeader, useToast, Input, FormControl, FormLabel,
  Table, Thead, Tbody, Tr, Th, Td, Badge, Divider, 
  Stat, StatLabel, StatNumber, StatGroup, InputGroup, InputLeftElement,
  Select, Alert, AlertIcon, Spinner, Icon,
  Tabs, TabList, TabPanels, Tab, TabPanel, Switch
} from '@chakra-ui/react';
import { 
  MdFilterList, MdDownload, MdSearch, MdRefresh,
  MdDescription, MdPeople, MdAttachMoney, MdCalendarToday,
  MdSort, MdFormatListNumbered
} from 'react-icons/md';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL
  : process.env.REACT_APP_LOCAL_API_URL;

const getToken = () => localStorage.getItem('sul_investimentos_token');

const PrecatorioReports = () => {
  // Form state - Basic filters
  const [state, setState] = useState('BA');
  const [maxYear, setMaxYear] = useState(new Date().getFullYear());
  const [groupingType, setGroupingType] = useState('CPF');
  const [minValue, setMinValue] = useState('');
  
  // Advanced filters state
  const [advState, setAdvState] = useState('BA');
  const [advMaxYear, setAdvMaxYear] = useState(new Date().getFullYear());
  const [advGroupingType, setAdvGroupingType] = useState('CPF');
  const [advMinValue, setAdvMinValue] = useState('');
  const [maxDocuments, setMaxDocuments] = useState('');
  const [sortBy, setSortBy] = useState('ano_mais_antigo');
  const [sortOrder, setSortOrder] = useState('asc');
  const [requireContact, setRequireContact] = useState(false);
  const [requireCPF, setRequireCPF] = useState(false);
  
  // Data state
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  
  const toast = useToast();
  
  // Format currency for display
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };
  
  // Handle basic report generation
  const handleGenerateReport = async () => {
    // Validate inputs
    if (!state || !groupingType) {
      toast({
        title: 'Dados incompletos',
        description: 'Por favor, preencha todos os campos obrigatórios',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setLoading(true);
    setReportData(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/reports/precatorios/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          state,
          maxYear: parseInt(maxYear),
          groupingType,
          minValue: parseFloat(minValue) || 0
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Erro ao gerar relatório');
      }
      
      if (data.success) {
        setReportData(data);
        
        if (data.data.length === 0) {
          toast({
            title: 'Nenhum resultado encontrado',
            description: 'Não foram encontrados precatórios com os filtros aplicados',
            status: 'info',
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: 'Relatório gerado',
            description: `${data.data.length} documentos encontrados`,
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast({
        title: 'Erro ao gerar relatório',
        description: error.message || 'Erro ao processar solicitação',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle advanced report generation
  const handleGenerateAdvancedReport = async () => {
    // Validate inputs
    if (!advState || !advGroupingType) {
      toast({
        title: 'Dados incompletos',
        description: 'Por favor, preencha todos os campos obrigatórios',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setLoading(true);
    setReportData(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/reports/precatorios/generate-advanced`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          state: advState,
          maxYear: parseInt(advMaxYear),
          groupingType: advGroupingType,
          minValue: parseFloat(advMinValue) || 0,
          maxDocuments: parseInt(maxDocuments) || 0,
          sortBy,
          sortOrder,
          requireContact,
          requireCPF
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Erro ao gerar relatório avançado');
      }
      
      if (data.success) {
        setReportData(data);
        
        if (data.data.length === 0) {
          toast({
            title: 'Nenhum resultado encontrado',
            description: 'Não foram encontrados precatórios com os filtros aplicados',
            status: 'info',
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: 'Relatório avançado gerado',
            description: `${data.data.length} documentos encontrados`,
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      console.error('Error generating advanced report:', error);
      toast({
        title: 'Erro ao gerar relatório avançado',
        description: error.message || 'Erro ao processar solicitação',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle CSV export for basic report
  const handleExportCSV = async () => {
    if (!reportData || reportData.data.length === 0) {
      toast({
        title: 'Nenhum dado para exportar',
        description: 'Gere um relatório primeiro',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setExporting(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/reports/precatorios/export`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          state,
          maxYear: parseInt(maxYear),
          groupingType,
          minValue: parseFloat(minValue) || 0
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao exportar relatório');
      }
      
      // Create blob from response
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `relatorio_precatorios_${groupingType}_${state}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: 'Exportação concluída',
        description: 'Arquivo CSV baixado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
    } catch (error) {
      console.error('Error exporting report:', error);
      toast({
        title: 'Erro ao exportar',
        description: error.message || 'Erro ao exportar relatório',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setExporting(false);
    }
  };
  
  // Handle CSV export for advanced report
  const handleExportAdvancedCSV = async () => {
    if (!reportData || reportData.data.length === 0) {
      toast({
        title: 'Nenhum dado para exportar',
        description: 'Gere um relatório primeiro',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setExporting(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/reports/precatorios/export-advanced`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          state: advState,
          maxYear: parseInt(advMaxYear),
          groupingType: advGroupingType,
          minValue: parseFloat(advMinValue) || 0,
          maxDocuments: parseInt(maxDocuments) || 0,
          sortBy,
          sortOrder,
          requireContact,
          requireCPF
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao exportar relatório avançado');
      }
      
      // Create blob from response
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `relatorio_precatorios_avancado_${advGroupingType}_${advState}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: 'Exportação concluída',
        description: 'Arquivo CSV avançado baixado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
    } catch (error) {
      console.error('Error exporting advanced report:', error);
      toast({
        title: 'Erro ao exportar',
        description: error.message || 'Erro ao exportar relatório avançado',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setExporting(false);
    }
  };
  
  // Reset form
  const handleReset = () => {
    if (activeTab === 0) {
      setState('BA');
      setMaxYear(new Date().getFullYear());
      setGroupingType('CPF');
      setMinValue('');
    } else {
      setAdvState('BA');
      setAdvMaxYear(new Date().getFullYear());
      setAdvGroupingType('CPF');
      setAdvMinValue('');
      setMaxDocuments('');
      setSortBy('ano_mais_antigo');
      setSortOrder('asc');
      setRequireContact(false);
      setRequireCPF(false);
    }
    setReportData(null);
  };
  
  return (
    <Box p={{ base: 4, md: 8 }}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box>
          <Heading size="lg" mb={2}>
            <Icon as={MdDescription} mr={2} />
            Relatórios de Precatórios
          </Heading>
          <Text color="gray.600">
            Gere relatórios agrupados por OAB, CPF ou CNPJ com filtros personalizados
          </Text>
        </Box>
        
        {/* Tabs for Basic and Advanced Filters */}
        <Tabs index={activeTab} onChange={setActiveTab} colorScheme="purple">
          <TabList>
            <Tab>Filtros Básicos</Tab>
            <Tab>Filtros Avançados</Tab>
          </TabList>
          
          <TabPanels>
            {/* Basic Filters Tab */}
            <TabPanel px={0}>
              <Card>
                <CardHeader>
                  <Heading size="md">
                    <Icon as={MdFilterList} mr={2} />
                    Filtros do Relatório
                  </Heading>
                </CardHeader>
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    {/* State Selection */}
                    <FormControl>
                      <FormLabel>Estado</FormLabel>
                      <Select value={state} onChange={(e) => setState(e.target.value)}>
                        <option value="BA">Bahia (BA)</option>
                        <option value="MA">Maranhão (MA)</option>
                      </Select>
                    </FormControl>
                    
                    {/* Grouping Type */}
                    <FormControl>
                      <FormLabel>Tipo de Agrupamento</FormLabel>
                      <RadioGroup value={groupingType} onChange={setGroupingType}>
                        <HStack spacing={6}>
                          <Radio value="OAB" colorScheme="purple">OAB</Radio>
                          <Radio value="CPF" colorScheme="purple">CPF</Radio>
                          <Radio value="CNPJ" colorScheme="purple">CNPJ</Radio>
                        </HStack>
                      </RadioGroup>
                    </FormControl>
                    
                    <HStack spacing={4}>
                      {/* Max Year */}
                      <FormControl flex={1}>
                        <FormLabel>Ano Máximo de Pagamento</FormLabel>
                        <InputGroup>
                          <InputLeftElement pointerEvents="none">
                            <Icon as={MdCalendarToday} color="gray.400" />
                          </InputLeftElement>
                          <Input
                            type="number"
                            value={maxYear}
                            onChange={(e) => setMaxYear(e.target.value)}
                            placeholder="Ex: 2024"
                            min="2000"
                            max="2050"
                          />
                        </InputGroup>
                      </FormControl>
                      
                      {/* Min Value */}
                      <FormControl flex={1}>
                        <FormLabel>Valor Mínimo (R$)</FormLabel>
                        <InputGroup>
                          <InputLeftElement pointerEvents="none">
                            <Icon as={MdAttachMoney} color="gray.400" />
                          </InputLeftElement>
                          <Input
                            type="number"
                            value={minValue}
                            onChange={(e) => setMinValue(e.target.value)}
                            placeholder="Ex: 10000"
                            min="0"
                            step="1000"
                          />
                        </InputGroup>
                      </FormControl>
                    </HStack>
                    
                    {/* Action Buttons */}
                    <HStack spacing={4} pt={2}>
                      <Button
                        colorScheme="purple"
                        leftIcon={<MdSearch />}
                        onClick={handleGenerateReport}
                        isLoading={loading}
                        loadingText="Gerando..."
                        size="lg"
                        flex={1}
                      >
                        Gerar Relatório
                      </Button>
                      
                      <Button
                        variant="outline"
                        leftIcon={<MdRefresh />}
                        onClick={handleReset}
                        isDisabled={loading}
                      >
                        Limpar
                      </Button>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>
            </TabPanel>
            
            {/* Advanced Filters Tab */}
            <TabPanel px={0}>
              <Card>
                <CardHeader>
                  <Heading size="md">
                    <Icon as={MdFilterList} mr={2} />
                    Filtros Avançados do Relatório
                  </Heading>
                </CardHeader>
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    {/* State Selection */}
                    <FormControl>
                      <FormLabel>Estado</FormLabel>
                      <Select value={advState} onChange={(e) => setAdvState(e.target.value)}>
                        <option value="BA">Bahia (BA)</option>
                        <option value="MA">Maranhão (MA)</option>
                      </Select>
                    </FormControl>
                    
                    {/* Grouping Type */}
                    <FormControl>
                      <FormLabel>Tipo de Agrupamento</FormLabel>
                      <RadioGroup value={advGroupingType} onChange={setAdvGroupingType}>
                        <HStack spacing={6}>
                          <Radio value="OAB" colorScheme="purple">OAB</Radio>
                          <Radio value="CPF" colorScheme="purple">CPF</Radio>
                          <Radio value="CNPJ" colorScheme="purple">CNPJ</Radio>
                        </HStack>
                      </RadioGroup>
                    </FormControl>
                    
                    <HStack spacing={4}>
                      {/* Max Year */}
                      <FormControl flex={1}>
                        <FormLabel>Ano Máximo de Pagamento</FormLabel>
                        <InputGroup>
                          <InputLeftElement pointerEvents="none">
                            <Icon as={MdCalendarToday} color="gray.400" />
                          </InputLeftElement>
                          <Input
                            type="number"
                            value={advMaxYear}
                            onChange={(e) => setAdvMaxYear(e.target.value)}
                            placeholder="Ex: 2024"
                            min="2000"
                            max="2050"
                          />
                        </InputGroup>
                      </FormControl>
                      
                      {/* Min Value */}
                      <FormControl flex={1}>
                        <FormLabel>Valor Mínimo (R$)</FormLabel>
                        <InputGroup>
                          <InputLeftElement pointerEvents="none">
                            <Icon as={MdAttachMoney} color="gray.400" />
                          </InputLeftElement>
                          <Input
                            type="number"
                            value={advMinValue}
                            onChange={(e) => setAdvMinValue(e.target.value)}
                            placeholder="Ex: 10000"
                            min="0"
                            step="1000"
                          />
                        </InputGroup>
                      </FormControl>
                    </HStack>
                    
                    <Divider />
                    
                    {/* Advanced Options */}
                    <HStack spacing={4}>
                      {/* Max Documents */}
                      <FormControl flex={1}>
                        <FormLabel>Máximo de Documentos</FormLabel>
                        <InputGroup>
                          <InputLeftElement pointerEvents="none">
                            <Icon as={MdFormatListNumbered} color="gray.400" />
                          </InputLeftElement>
                          <Input
                            type="number"
                            value={maxDocuments}
                            onChange={(e) => setMaxDocuments(e.target.value)}
                            placeholder="Ex: 100 (vazio = todos)"
                            min="0"
                          />
                        </InputGroup>
                      </FormControl>
                      
                      {/* Sort By */}
                      <FormControl flex={1}>
                        <FormLabel>Ordenar Por</FormLabel>
                        <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                          <option value="ano_mais_antigo">Ano Mais Antigo</option>
                          <option value="qtd_precatorios">Quantidade de Precatórios</option>
                          <option value="valor_total">Valor Total</option>
                          <option value="valor_por_precatorio">Valor por Precatório</option>
                          <option value="quantidade_e_valor">Quantidade e Valor (Combinado)</option>
                        </Select>
                      </FormControl>
                    </HStack>
                    
                    {/* Sort Order */}
                    <FormControl>
                      <FormLabel>
                        <Icon as={MdSort} mr={2} />
                        Ordem de Classificação
                      </FormLabel>
                      <RadioGroup value={sortOrder} onChange={setSortOrder}>
                        <HStack spacing={6}>
                          <Radio value="asc" colorScheme="purple">Crescente</Radio>
                          <Radio value="desc" colorScheme="purple">Decrescente</Radio>
                        </HStack>
                      </RadioGroup>
                    </FormControl>
                    
                    {/* Contact Filter */}
                    <FormControl>
                      <HStack justify="space-between">
                        <FormLabel htmlFor="contact-filter" mb={0}>
                          <Icon as={MdPeople} mr={2} />
                          Somente documentos com contato (Email ou Telefone)
                        </FormLabel>
                        <Switch
                          id="contact-filter"
                          colorScheme="purple"
                          isChecked={requireContact}
                          onChange={(e) => setRequireContact(e.target.checked)}
                        />
                      </HStack>
                    </FormControl>
                    
                    {/* CPF Filter - Only show for OAB grouping */}
                    {advGroupingType === 'OAB' && (
                      <FormControl>
                        <HStack justify="space-between">
                          <FormLabel htmlFor="cpf-filter" mb={0}>
                            <Icon as={MdPeople} mr={2} />
                            Somente documentos OAB com CPF
                          </FormLabel>
                          <Switch
                            id="cpf-filter"
                            colorScheme="purple"
                            isChecked={requireCPF}
                            onChange={(e) => setRequireCPF(e.target.checked)}
                          />
                        </HStack>
                      </FormControl>
                    )}
                    
                    {/* Action Buttons */}
                    <HStack spacing={4} pt={2}>
                      <Button
                        colorScheme="purple"
                        leftIcon={<MdSearch />}
                        onClick={handleGenerateAdvancedReport}
                        isLoading={loading}
                        loadingText="Gerando..."
                        size="lg"
                        flex={1}
                      >
                        Gerar Relatório Avançado
                      </Button>
                      
                      <Button
                        variant="outline"
                        leftIcon={<MdRefresh />}
                        onClick={handleReset}
                        isDisabled={loading}
                      >
                        Limpar
                      </Button>
                    </HStack>
                  </VStack>
                </CardBody>
              </Card>
            </TabPanel>
          </TabPanels>
        </Tabs>
        
        {/* Results */}
        {reportData && reportData.success && (
          <>
            {/* Summary Stats */}
            <Card bg="purple.50" borderColor="purple.200" borderWidth="1px">
              <CardBody>
                <VStack align="stretch" spacing={4}>
                  <HStack justify="space-between">
                    <Heading size="sm">Resumo do Relatório</Heading>
                    <Button
                      size="sm"
                      colorScheme="green"
                      leftIcon={<MdDownload />}
                      onClick={activeTab === 0 ? handleExportCSV : handleExportAdvancedCSV}
                      isLoading={exporting}
                      loadingText="Exportando..."
                    >
                      Exportar CSV
                    </Button>
                  </HStack>
                  
                  <StatGroup>
                    <Stat>
                      <StatLabel>Total de Documentos</StatLabel>
                      <StatNumber>{reportData.summary.total_documents}</StatNumber>
                    </Stat>
                    
                    <Stat>
                      <StatLabel>Total de Precatórios</StatLabel>
                      <StatNumber>{reportData.summary.total_precatorios}</StatNumber>
                    </Stat>
                    
                    <Stat>
                      <StatLabel>Valor Total</StatLabel>
                      <StatNumber fontSize="lg">
                        {formatCurrency(reportData.summary.total_value)}
                      </StatNumber>
                    </Stat>
                  </StatGroup>
                  
                  <Divider />
                  
                  <HStack fontSize="sm" color="purple.700" flexWrap="wrap">
                    <Badge colorScheme="purple">Estado: {reportData.filters.state}</Badge>
                    <Badge colorScheme="purple">Agrupamento: {reportData.filters.groupingType}</Badge>
                    <Badge colorScheme="purple">Ano Máx: {reportData.filters.maxYear}</Badge>
                    {reportData.filters.minValue > 0 && (
                      <Badge colorScheme="purple">
                        Valor Mín: {formatCurrency(reportData.filters.minValue)}
                      </Badge>
                    )}
                    {reportData.filters.maxDocuments > 0 && (
                      <Badge colorScheme="orange">
                        Máx Docs: {reportData.filters.maxDocuments}
                      </Badge>
                    )}
                    {reportData.filters.sortBy && (
                      <Badge colorScheme="orange">
                        Ordenado por: {
                          reportData.filters.sortBy === 'qtd_precatorios' ? 'Quantidade' :
                          reportData.filters.sortBy === 'valor_total' ? 'Valor Total' :
                          reportData.filters.sortBy === 'valor_por_precatorio' ? 'Valor/Precatório' :
                          reportData.filters.sortBy === 'quantidade_e_valor' ? 'Qtd × Valor' :
                          'Ano Mais Antigo'
                        }
                      </Badge>
                    )}
                    {reportData.filters.sortOrder && (
                      <Badge colorScheme="orange">
                        {reportData.filters.sortOrder === 'desc' ? 'Decrescente' : 'Crescente'}
                      </Badge>
                    )}
                  </HStack>
                </VStack>
              </CardBody>
            </Card>
            
            {/* Results Table */}
            {reportData.data.length > 0 && (
              <Card>
                <CardBody>
                  <Box overflowX="auto">
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Nome</Th>
                          <Th>{reportData.filters.groupingType || groupingType}</Th>
                          <Th>Cidade</Th>
                          <Th>Telefone</Th>
                          <Th>Email</Th>
                          <Th isNumeric>Qtd</Th>
                          <Th isNumeric>Valor Total</Th>
                          {reportData.data[0].valor_por_precatorio !== undefined && (
                            <Th isNumeric>Valor/Precatório</Th>
                          )}
                          <Th isNumeric>Ano + Antigo</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {reportData.data.slice(0, 50).map((doc, index) => (
                          <Tr key={index}>
                            <Td>
                              <Text fontWeight="medium" maxW="200px" isTruncated>
                                {doc.name}
                              </Text>
                            </Td>
                            <Td>
                              <Text fontSize="xs" maxW="150px" isTruncated>
                                {doc.identifier}
                              </Text>
                            </Td>
                            <Td>
                              <Text fontSize="xs">
                                {doc.cidade || '-'}
                              </Text>
                            </Td>
                            <Td>
                              <Text fontSize="xs">
                                {doc.phone || '-'}
                              </Text>
                            </Td>
                            <Td>
                              <Text fontSize="xs" maxW="200px" isTruncated>
                                {doc.email || '-'}
                              </Text>
                            </Td>
                            <Td isNumeric>
                              <Badge colorScheme="blue">{doc.qtd_precatorios}</Badge>
                            </Td>
                            <Td isNumeric>
                              <Text fontWeight="medium">
                                {formatCurrency(doc.valor_total)}
                              </Text>
                            </Td>
                            {doc.valor_por_precatorio !== undefined && (
                              <Td isNumeric>
                                <Text fontSize="sm">
                                  {formatCurrency(doc.valor_por_precatorio)}
                                </Text>
                              </Td>
                            )}
                            <Td isNumeric>
                              <Badge colorScheme="orange">{doc.ano_mais_antigo}</Badge>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                    
                    {reportData.data.length > 50 && (
                      <Alert status="info" mt={4}>
                        <AlertIcon />
                        <Text fontSize="sm">
                          Mostrando apenas os primeiros 50 resultados. 
                          Exporte o CSV para ver todos os {reportData.data.length} registros.
                        </Text>
                      </Alert>
                    )}
                  </Box>
                </CardBody>
              </Card>
            )}
          </>
        )}
        
        {/* Loading State */}
        {loading && (
          <Card>
            <CardBody>
              <VStack spacing={4} py={8}>
                <Spinner size="xl" color="purple.500" thickness="4px" />
                <Text>Gerando relatório...</Text>
              </VStack>
            </CardBody>
          </Card>
        )}
      </VStack>
    </Box>
  );
};

export default PrecatorioReports;